import * as Sentry from "@sentry/browser";
import React from "react";
import { Redirect, Route, Switch } from "react-router-dom";

import { AxiosResponse } from "axios";
import { RouteComponentProps, withRouter } from "react-router";
import FeedbackForm from "./components/FeedbackForm";
import { Page } from "./components/Page";
import routes from "./config/routes";
import {
  BuildAndReleaseSchema,
  Entitlement,
  Ticket,
  User as UserInterface,
} from "./helpers/shared-interfaces";
import { checkPermission, request } from "./helpers/utilities";
import Unauthorized from "./pages/Unauthorized";

import { message } from "antd";
import KeyboardShortcutsButton from "./components/KeyboardShortcutsButton";
import Sidebar from "./components/Sidebar";
import MenuList from "./config/sidebarMenuList";
import { generateDisplayErrorMessage } from "./helpers/utilities";


interface AuthorizedPageCreatorProps {
  entitlement: Entitlement;
  updateLoggedInStatus: (value: boolean) => void;
  releaseInfo: BuildAndReleaseSchema | null;
  isLoading: boolean;
  route: { title: string; requiresAuth: boolean; };
  user: UserInterface;
  logout: () => void;
  Component: typeof React.Component;
}

interface UnauthenticatedPageCreatorProps {
  updateLoggedInStatus: (value: boolean) => void;
  Component: typeof React.Component;
}

export function AuthorizedPageCreator({
  releaseInfo,
  route,
  user,
  logout,
  isLoading,
  updateLoggedInStatus,
  Component,
  entitlement,
}: AuthorizedPageCreatorProps): (props: RouteComponentProps) => JSX.Element {
  return (props: RouteComponentProps): JSX.Element => {
    if (route.requiresAuth) {
      return (
        <Page
          releaseInfo={releaseInfo}
          isLoading={isLoading}
          title={route.title}
          logout={logout}
          user={user}
        >
          <Component
            updateLoggedInStatus={updateLoggedInStatus}
            entitlement={entitlement}
            user={user}
            {...props}
          />
        </Page>
      );
    } else {
      return (
        <Component
          updateLoggedInStatus={updateLoggedInStatus}
          user={user}
          {...props}
        />
      );
    }
  };
}

export function UnauthenticatedPageCreator({
  updateLoggedInStatus,
  Component,
}: UnauthenticatedPageCreatorProps): (
  props: RouteComponentProps,
) => JSX.Element {
  return function WrappedComponent(props: RouteComponentProps): JSX.Element {
    return <Component {...props} updateLoggedInStatus={updateLoggedInStatus} />;
  };
}

export const CURRENT_USER_QUERY = `
  query {
    currentUser {
      id
      name
      email
      role {
        id
        name
      }
      department {
        id
        name
      }
      entitlements {
        id
        name
        entityExceptions {
          id
          entityId
          user {
            id
            email
          }
          exceptionType
        }
      }
      userNotifications {
        id
        message
        createdAt
      }
    }
  }
`;

export const BUILD_AND_RELEASE_VERSION_QUERY = `
  query   {
    release {
        apiVersion
        webVersion
        environment
      }
  }

`;

const CREATE_FEEDBACK_MUTATION = `
mutation CreateBugTicket($summary: String!, $description: String!) {
  createBugTicket(summary: $summary, description: $description){
    ticket {
      id
    }
    errors {
      property
      description
    }
  }
}
`;

export const LOGOUT_MUTATION = `
mutation LogOut {
  logout{
    errors{
      description
    }
    success
  }
}
`;

interface State {
  token: string | null;
  user: UserInterface | null;
  isLoggedIn: boolean;
  isLoading: boolean;
  releaseInfo: BuildAndReleaseSchema | null;
  isTestUser: boolean;
  showFeedbackForm: boolean;
  sendingFeedback: boolean;
}

export class App extends React.Component<RouteComponentProps, State> {
  constructor(props: Readonly<RouteComponentProps>) {
    super(props);
    this.state = {
      isLoading: false,
      token: null,
      user: null,
      isLoggedIn: false,
      releaseInfo: null,
      isTestUser: false,
      showFeedbackForm: false,
      sendingFeedback: false,
    };
    this.submitFeedback = this.submitFeedback.bind(this);
    this.getBuildAndReleaseVersion = this.getBuildAndReleaseVersion.bind(this);
    this.updateLoggedInStatus = this.updateLoggedInStatus.bind(this);
    this.loadCurrentUserData = this.loadCurrentUserData.bind(this);
    this.logout = this.logout.bind(this);
  }

  componentDidUpdate(prevProps: RouteComponentProps): void {
    if (
      this.props.location.pathname !== prevProps.location.pathname &&
      this.props.location.pathname !== "/login"
    ) {
      this.loadCurrentUserData();
    }
  }

  async getBuildAndReleaseVersion(): Promise<void> {
    interface Response {
      data: {
        release: BuildAndReleaseSchema;
      };
    }

    try {
      const response: AxiosResponse<Response> = await request({
        query: BUILD_AND_RELEASE_VERSION_QUERY,
      });
      response.data.data.release &&
        (this.setState({ releaseInfo: response.data.data.release }));
    } catch (error) {
      Sentry.captureException(error);
    }
  }

  async logout(): Promise<void> {
    this.setState({ isLoggedIn: false });
    request({
      query: LOGOUT_MUTATION,
    })
      .then(async (): Promise<void> => {
        localStorage.clear();
        window.location.assign("/login");
      })
      .catch((error): void => {
        Sentry.captureException(error);
      });
  }

  loadCurrentUserData(): void {
    request({
      query: CURRENT_USER_QUERY,
    })
      .then(async ({ data }: AxiosResponse<any>): Promise<void> => {
        const user = data.data.currentUser;
        localStorage.setItem("user", JSON.stringify(user));
        this.setState({ user });
      })
      .catch((): void => {
        this.logout();
      });
  }

  async updateLoggedInStatus(value: boolean): Promise<void> {
    const { user, token } = this.loadTokenAndUserFromLocalStorage();

    this.setState({ token, user, isLoggedIn: value });
  }

  async UNSAFE_componentWillMount(): Promise<void> {
    const { user, token } = this.loadTokenAndUserFromLocalStorage();

    this.setState({ token, user });
  }

  loadTokenAndUserFromLocalStorage = (): {
    user: UserInterface | null;
    token: string | null;
  } => {
    const token: string | null = JSON.parse(
      localStorage.getItem("token") as string,
    );
    const user: UserInterface | null = JSON.parse(
      localStorage.getItem("user") as string,
    );

    return { token, user };
  };

  componentDidMount(): void {
    const { token } = this.loadTokenAndUserFromLocalStorage();

    if (token) {
      this.getBuildAndReleaseVersion();
      this.loadCurrentUserData();
    }
  }

  async submitFeedback<T>(values: T): Promise<void> {
    this.setState({ sendingFeedback: true });
    interface Response {
      data: {
        createBugTicket: {
          ticket: Ticket;
        };
      };
    }
    try {
      const {
        data: { data },
      }: AxiosResponse<Response> = await request({
        query: CREATE_FEEDBACK_MUTATION,
        variables: values as unknown as Record<string, unknown>,
      });
      if (!data.createBugTicket.ticket.id) {
        throw new Error("Feedback Error");
      }
      message.success("Your feedback was sent successfully!");
      this.toggleFeedbackForm();
    } catch (e) {
      message.error(`Failed To Send Feedback - ${generateDisplayErrorMessage(e)}`);
    }

    this.setState({ sendingFeedback: false });
  }

  toggleFeedbackForm = (): void => {
    this.setState((prevState) => ({
      showFeedbackForm: !prevState.showFeedbackForm,
    }));
  };

  setScope = (user: UserInterface): ((scope: Sentry.Scope) => void) => {
    return (scope: Sentry.Scope): void => {
      scope.setUser({ email: user.email });
    };
  };

  render(): JSX.Element {
    const { user, isLoading, releaseInfo, showFeedbackForm, sendingFeedback } =
      this.state;

    const children: JSX.Element[] = [];

    if (user) {
      Sentry.configureScope(this.setScope(user));

      for (const route of routes) {
        let Component: typeof React.Component;
        const { canAccess, entitlement } = checkPermission(
          user,
          route.entitlement,
        );
        if (canAccess || !route.requiresAuth) {
          Component = route.component;
        } else {
          Component = Unauthorized;
        }
        children.push(
          <Route
            render={AuthorizedPageCreator({
              updateLoggedInStatus: this.updateLoggedInStatus,
              entitlement: entitlement as Entitlement,
              logout: this.logout,
              releaseInfo,
              isLoading,
              Component,
              route,
              user,
            })}
            exact={route.exact}
            path={route.path}
            key={
              route.path.includes(":")
                ? `${route.path}-${this.props.location.pathname}`
                : route.path
            }
          />,
        );
      }
    } else {
      for (const route of routes) {
        if (!route.requiresAuth) {
          const Component = route.component;
          children.push(
            <Route
              render={UnauthenticatedPageCreator({
                updateLoggedInStatus: this.updateLoggedInStatus,
                Component,
              })}
              exact={route.exact}
              path={route.path}
              key={route.path}
            />,
          );
        }
      }
      children.push(
        <Redirect
          key="login"
          to={{
            pathname: "/login",
            state: { from: this.props.location },
          }}
        />,
      );
    }
    return (
      <>
        <div className="app-container">
          {user && <Sidebar menuList={MenuList} />}
          <Switch>{children}</Switch>
          {showFeedbackForm && (
            <FeedbackForm
              handleSubmit={this.submitFeedback}
              isLoading={sendingFeedback}
            />
          )}
        </div>
        {user && <KeyboardShortcutsButton />}
        {/* {user && (
          <ChatwootWidget
            baseUrl="https://chatwoot.petra-world.com"
            user={user}
            settings={{
              position: "right",
              type: "standard",
            }}
          />
        )} */}
      </>
    );
  }
}

export default withRouter(App);
